<template>
  <div class="w-full">
    <div
      class="flex flex-wrap gap-1 items-center border border-gray-400 rounded-md p-1 bg-white cursor-text"
      @click="inputEl && inputEl.focus()"
    >
      <span
        v-for="tag in modelValue"
        :key="tag.id || tag.name"
        class="inline-flex items-center bg-accent-primary text-white text-xs rounded px-2 py-0.5"
      >
        {{ tag.name }}
        <button
          v-if="!disabled"
          @click.prevent="removeTag(tag)"
          type="button"
          class="ml-1 text-white/80 hover:text-white"
          aria-label="Remove tag"
        >×</button>
      </span>
      <input
        ref="inputEl"
        v-model="query"
        type="text"
        :disabled="disabled"
        :placeholder="modelValue.length ? '' : (placeholder || 'Add tag…')"
        class="flex-1 min-w-[8rem] px-1 py-0.5 outline-none text-sm bg-transparent"
        @keydown.enter.prevent="onEnter"
        @keydown.delete="onBackspace"
        @keydown.down.prevent="moveHighlight(1)"
        @keydown.up.prevent="moveHighlight(-1)"
        @focus="showSuggestions = true"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="showSuggestions && (filteredSuggestions.length || canCreateNew)"
      class="relative"
    >
      <ul
        class="absolute z-50 left-0 right-0 mt-0.5 max-h-56 overflow-auto bg-white border border-gray-300 rounded shadow text-sm"
        @mousedown.prevent
      >
        <li
          v-for="(tag, idx) in filteredSuggestions"
          :key="tag.id"
          @click="selectTag(tag)"
          @mouseenter="highlightedIndex = idx"
          :class="{
            'bg-gray-100': highlightedIndex === idx,
            'cursor-pointer px-2 py-1 flex items-center justify-between': true,
          }"
        >
          <span>{{ tag.name }}</span>
          <span v-if="tag.usage_count != null" class="text-xs text-gray-500 ml-2">{{ tag.usage_count }}</span>
        </li>
        <li
          v-if="canCreateNew"
          @click="createAndSelect"
          @mouseenter="highlightedIndex = filteredSuggestions.length"
          :class="{
            'bg-gray-100': highlightedIndex === filteredSuggestions.length,
            'cursor-pointer px-2 py-1 italic text-gray-700': true,
          }"
        >
          Create new tag <span class="font-semibold not-italic">"{{ query.trim() }}"</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default { name: 'tag-picker' }
</script>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { listExpenseTags } from '@/services/ExpenseTag.js'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'add', 'remove'])

const store = useStore()
const token = computed(() => store.state.auth.token)

const query = ref('')
const allTags = ref([])
const showSuggestions = ref(false)
const highlightedIndex = ref(0)
const inputEl = ref(null)

onMounted(loadTags)

async function loadTags() {
  try {
    const data = await listExpenseTags(token.value)
    allTags.value = Array.isArray(data) ? data : (data?.results || [])
  } catch (_) {
    allTags.value = []
  }
}

const selectedIds = computed(() => new Set(props.modelValue.map((t) => t.id).filter((x) => x != null)))
const selectedNamesLower = computed(() => new Set(props.modelValue.map((t) => (t.name || '').toLowerCase())))

const filteredSuggestions = computed(() => {
  const q = query.value.trim().toLowerCase()
  return allTags.value.filter((t) => {
    if (selectedIds.value.has(t.id)) return false
    if (selectedNamesLower.value.has((t.name || '').toLowerCase())) return false
    if (!q) return true
    return (t.name || '').toLowerCase().includes(q)
  }).slice(0, 20)
})

const canCreateNew = computed(() => {
  const q = query.value.trim()
  if (!q) return false
  if (selectedNamesLower.value.has(q.toLowerCase())) return false
  return !allTags.value.some((t) => (t.name || '').toLowerCase() === q.toLowerCase())
})

watch(query, () => { highlightedIndex.value = 0 })

function selectTag(tag) {
  if (selectedIds.value.has(tag.id)) return
  emit('update:modelValue', [...props.modelValue, { id: tag.id, name: tag.name }])
  emit('add', tag)
  query.value = ''
  inputEl.value?.focus()
}

function createAndSelect() {
  const name = query.value.trim()
  if (!name) return
  // Optimistic local add — parent decides whether to persist (form submit)
  // or call addTagsToExpense() immediately (live edit mode).
  emit('update:modelValue', [...props.modelValue, { id: null, name }])
  emit('add', { id: null, name })
  query.value = ''
  inputEl.value?.focus()
}

function removeTag(tag) {
  emit('update:modelValue', props.modelValue.filter((t) => (t.id ?? t.name) !== (tag.id ?? tag.name)))
  emit('remove', tag)
}

function onEnter() {
  const total = filteredSuggestions.value.length + (canCreateNew.value ? 1 : 0)
  if (total === 0) return
  if (highlightedIndex.value < filteredSuggestions.value.length) {
    selectTag(filteredSuggestions.value[highlightedIndex.value])
  } else {
    createAndSelect()
  }
}

function onBackspace(e) {
  if (query.value === '' && props.modelValue.length) {
    const last = props.modelValue[props.modelValue.length - 1]
    removeTag(last)
    e.preventDefault()
  }
}

function moveHighlight(delta) {
  const total = filteredSuggestions.value.length + (canCreateNew.value ? 1 : 0)
  if (total === 0) return
  highlightedIndex.value = ((highlightedIndex.value + delta) + total) % total
  showSuggestions.value = true
}

function onBlur() {
  setTimeout(() => { showSuggestions.value = false }, 100)
}
</script>